import React, { useState, useEffect } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Badge, 
  Modal, 
  Toast,
  ToastContainer,
  Spinner,
  FloatingLabel,
  ProgressBar
} from "react-bootstrap";
import axios from "axios";
import {
  Search,
  PlusCircle,
  Eye,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  ExclamationCircle,
  Layers,
  Activity,
  Tag,
  List,
  Folder,
  FolderPlus,
  InfoCircle,
  ArrowLeft,
  Calendar,
  Clock,
  People,
  GraphUp,
  FileText,
  Cloud,
  BarChartFill,
  Globe
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";


const CATEGORIES_ENDPOINT = `${API_BASE_URL}/usager-categories`;

export default function CategoriesUsagers() {
  const navigate = useNavigate();

  // États principaux
  const [categories, setCategories] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour les modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // États pour les données des modals
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editedCategory, setEditedCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    code_category: ""
  });
  
  // États pour les chargements
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  
  // États pour les notifications
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  // Obtenir le token d'authentification
  const getAuthToken = () => sessionStorage.getItem("authToken");

  // Headers pour les requêtes API
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  // Charger les catégories depuis l'API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(CATEGORIES_ENDPOINT, {
        headers: getHeaders(),
      });
      
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setCategories(response.data.data);
      } else {
        throw new Error('Format de données invalide');
      }
    } catch (error) {
      setError(`${error.response?.data?.message || error.message}`);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialiser les données
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fonctions pour les notifications
  const showAlert = (message, type = 'info', duration = 4000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => {
        setAlertMessage(prev => ({ ...prev, show: false }));
      }, duration);
    }
  };

  const hideAlert = () => {
    setAlertMessage(prev => ({ ...prev, show: false }));
  };

  const handleRetry = () => {
    setError(null);
    fetchCategories();
  };

  // Filtrage et tri des données
  const filteredAndSortedItems = React.useMemo(() => {
    let filtered = [...categories];
    
    if (filterText) {
      const searchTerm = filterText.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name && item.name.toLowerCase().includes(searchTerm)) ||
        (item.description && item.description.toLowerCase().includes(searchTerm)) ||
        (item.code_category && item.code_category.toString().includes(searchTerm))
      );
    }

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'name') {
        aValue = a.name || '';
        bValue = b.name || '';
      } else if (sortField === 'code_category') {
        aValue = a.code_category || '';
        bValue = b.code_category || '';
      } else if (sortField === 'created_at') {
        aValue = a.created_at || '';
        bValue = b.created_at || '';
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [categories, filterText, sortField, sortDirection]);

  // Statistiques
  const stats = React.useMemo(() => {
    const total = categories.length;
    const withDescription = categories.filter(c => c.description && c.description.trim() !== '').length;
    const descriptionRate = total > 0 ? Math.round((withDescription / total) * 100) : 0;
    
    return { 
      total, 
      withDescription,
      descriptionRate
    };
  }, [categories]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  // Fonctions de tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCategories(currentItems.map(category => category.id));
    } else {
      setSelectedCategories([]);
    }
  };

  const handleSelectCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedCategories.length === currentItems.length;
  const isSomeSelected = selectedCategories.length > 0 && selectedCategories.length < currentItems.length;

  // Fonctions de formatage
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtenir la couleur du badge
  const getCategoryColor = (id) => {
    const colors = ["primary", "success", "info", "warning", "dark", "secondary"];
    return colors[Math.abs(id) % colors.length];
  };

  // Obtenir le badge de catégorie
  const getCategoryBadge = (category) => {
    const color = getCategoryColor(category.id || 0);
    return (
      <Badge bg={color} className="px-3 py-2 rounded-pill">
        {category.name}
      </Badge>
    );
  };

  // Gestion de la suppression
  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setLoadingDelete(true);
      
      await axios.delete(`${CATEGORIES_ENDPOINT}/${categoryToDelete.code_category}`, {
        headers: getHeaders(),
      });

      setCategories(prev => prev.filter(c => c.code_category !== categoryToDelete.code_category));
      setSelectedCategories(prev => prev.filter(code_category => code_category !== categoryToDelete.code_category));
      showAlert('Catégorie supprimée avec succès', 'success');
    } catch (error) {
      console.error("Erreur suppression:", error);
      showAlert(`Erreur lors de la suppression: ${error.message}`, 'danger');
    } finally {
      setLoadingDelete(false);
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  // Gestion de la modification
  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setEditedCategory({...category});
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editedCategory) return;

    try {
      setLoadingUpdate(true);
      
      await axios.put(
        `${CATEGORIES_ENDPOINT}/${editedCategory.code_category}`,
        {
          name: editedCategory.name,
          description: editedCategory.description
        },
        { headers: getHeaders() }
      );

      setCategories(prev => prev.map(c => 
        c.code_category === editedCategory.code_category ? editedCategory : c
      ));
      
      setShowEditModal(false);
      showAlert('Catégorie modifiée avec succès', 'success');
    } catch (error) {
      console.error("Erreur modification:", error);
      showAlert(`Erreur lors de la modification: ${error.message}`, 'danger');
    } finally {
      setLoadingUpdate(false);
    }
  };

  // Gestion de l'ajout
  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      showAlert('Le nom de la catégorie est requis', 'warning');
      return;
    }

    try {
      setLoadingAdd(true);
      
      const payload = {
        name: newCategory.name,
        description: newCategory.description
      };
      
      const response = await axios.post(
        CATEGORIES_ENDPOINT,
        payload,
        { headers: getHeaders() }
      );

      setCategories(prev => [...prev, response.data]);
      
      setShowAddModal(false);
      setNewCategory({
        name: "",
        description: "",
        code_category: ""
      });

      await fetchCategories();
      showAlert('Catégorie ajoutée avec succès', 'success');
    } catch (error) {
      console.error("Erreur ajout:", error);
      showAlert(`Erreur lors de l'ajout: ${error.message}`, 'danger');
    } finally {
      setLoadingAdd(false);
    }
  };

  // Voir les détails
  const handleViewDetails = (category) => {
    navigate(`/admin/usager-categories/${category.code_category}`, { state: { category } });
  };

  // Composant pour l'en-tête triable
  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  // Fonction pour rafraîchir les données
  const handleRefresh = () => {
    fetchCategories();
  };

  return (
    <React.Fragment>
      <Header />
      
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={alertMessage.show} onClose={hideAlert} bg={alertMessage.type} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            {alertMessage.type === 'success' && <CheckCircle className="me-2 text-success" />}
            {alertMessage.type === 'danger' && <XCircle className="me-2 text-danger" />}
            {alertMessage.type === 'warning' && <ExclamationCircle className="me-2 text-warning" />}
            {alertMessage.type === 'info' && <CheckCircle className="me-2 text-info" />}
            <strong className="me-auto">Notification</strong>
            <small>maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-white fw-medium">{alertMessage.message}</Toast.Body>
        </Toast>
      </ToastContainer>
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div className="container-fluid">
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Catégories d'activités</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <Folder className="me-2 text-primary" size={28} />
                    Gestion des Catégories d'activité
                  </h4>
                  <p className="text-muted mb-0">Consultez et gérez toutes les catégories d'activités</p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {error && (
                    <Button variant="warning" size="sm" onClick={handleRefresh} className="d-flex align-items-center">
                      <ExclamationCircle className="me-2" />Réessayer
                    </Button>
                  )}
                  
                  {/* {selectedCategories.length > 0 && (
                    <Button variant="outline-danger" size="sm" onClick={() => {
                      const toDelete = categories.filter(c => selectedCategories.includes(c.id));
                      if (toDelete.length > 0) {
                        setCategoryToDelete(toDelete[0]);
                        setShowDeleteModal(true);
                      }
                    }} className="d-flex align-items-center">
                      <Trash className="me-2" size={16} />Supprimer ({selectedCategories.length})
                    </Button>
                  )} */}
                  
                  <Button variant="success" onClick={() => setShowAddModal(true)} className="d-flex align-items-center shadow-sm">
                    <FolderPlus className="me-2" size={16} />Nouvelle Catégorie
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Cartes de statistiques */}
          {/* <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-primary"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                    <Folder size={28} className="text-primary" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Catégories</h6>
                    <h3 className="mb-0 fw-bold">{stats.total}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-success"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                    <FileText size={28} className="text-success" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Avec description</h6>
                    <h3 className="mb-0 fw-bold">{stats.withDescription}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-info"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                    <BarChartFill size={28} className="text-info" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Taux de complétion</h6>
                    <h3 className="mb-0 fw-bold">{stats.descriptionRate}%</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row> */}

          {/* Barre de recherche */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Search className="text-primary" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher par nom, description ou code..."
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          className="border-start-0 bg-light"
                        />
                        {filterText && (
                          <Button variant="outline-secondary" className="border-start-0" onClick={() => setFilterText('')}>
                            <X size={16} />
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="bg-light"
                      >
                        <option value="5">5 / page</option>
                        <option value="10">10 / page</option>
                        <option value="25">25 / page</option>
                        <option value="50">50 / page</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setFilterText("");
                            setCurrentPage(1);
                            setSelectedCategories([]);
                          }}
                          className="flex-grow-1 d-flex align-items-center justify-content-center"
                        >
                          <X className="me-2" size={16} />Réinitialiser
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={handleRefresh}
                          className="d-flex align-items-center justify-content-center"
                          disabled={loading}
                        >
                          <Cloud size={16} />
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <Folder className="me-2 text-white" />
                    <span className="fw-semibold text-white">Liste des Catégories d'activités ({filteredAndSortedItems.length})</span>
                    {filterText && <Badge bg="info" className="ms-2 rounded-pill">Filtre actif</Badge>}
                  </div>
                  <div className="text-white small d-flex align-items-center">
                    {selectedCategories.length > 0 && (
                      <span className="me-3">{selectedCategories.length} sélectionné(s)</span>
                    )}
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                              checked={isAllSelected}
                              ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>ID</th>
                          <SortableHeader field="code_category">Code</SortableHeader>
                          <SortableHeader field="name">Nom</SortableHeader>
                          <th>Description</th>
                          <SortableHeader field="created_at">Créé le</SortableHeader>
                          <th style={{ width: '130px' }} className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && categories.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <Spinner animation="border" variant="primary" />
                              <p className="mt-2 text-muted">Chargement des catégories...</p>
                            </td>
                          </tr>
                        ) : error && categories.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <ExclamationCircle size={48} className="text-danger mb-3" />
                              <h5>Erreur de chargement</h5>
                              <p>{error}</p>
                              <Button variant="primary" onClick={handleRefresh} className="mt-3">Réessayer</Button>
                            </td>
                          </tr>
                        ) : currentItems.length > 0 ? (
                          currentItems.map((category) => {
                            const color = getCategoryColor(category.id);
                            return (
                              <tr key={category.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                                    checked={selectedCategories.includes(category.id)}
                                    onChange={() => handleSelectCategory(category.id)}
                                  />
                                </td>
                                <td>
                                  <Badge bg="light" text="dark" className="fw-semibold">
                                    {category.id}
                                  </Badge>
                                </td>
                                <td>
                                  <code className="text-primary">{category.code_category}</code>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Folder size={16} className="text-primary me-2" />
                                    <strong className="d-block">{category.name}</strong>
                                  </div>
                                </td>
                                <td>
                                  <span className="text-muted small">
                                    {category.description 
                                      ? (category.description.length > 50 
                                        ? category.description.substring(0, 50) + '...' 
                                        : category.description)
                                      : "Aucune description"}
                                  </span>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {formatDate(category.created_at)}
                                  </small>
                                </td>
                                <td>
                                  <div className="d-flex gap-1 justify-content-center">
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      title="Voir détails"
                                      onClick={() => handleViewDetails(category)}
                                      className="rounded-circle"
                                      style={{ width: '34px', height: '34px', padding: 0 }}
                                    >
                                      <Eye size={14} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      title="Modifier"
                                      onClick={() => handleEditCategory(category)}
                                      className="rounded-circle"
                                      style={{ width: '34px', height: '34px', padding: 0 }}
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                    {/* <Button
                                      size="sm"
                                      variant="outline-danger"
                                      title="Supprimer"
                                      onClick={() => handleDeleteCategory(category)}
                                      className="rounded-circle"
                                      style={{ width: '34px', height: '34px', padding: 0 }}
                                    >
                                      <Trash size={14} />
                                    </Button> */}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <Folder size={48} className="text-muted opacity-25" />
                              <h5 className="mt-3 text-muted">AUCUNE CATÉGORIE TROUVÉE</h5>
                              <p className="text-muted small">Aucune catégorie ne correspond à vos critères</p>
                              {filterText && (
                                <Button variant="outline-primary" size="sm" onClick={() => setFilterText('')}>
                                  Réinitialiser la recherche
                                </Button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                      <div className="text-muted small">
                        Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong> catégories
                        {selectedCategories.length > 0 && (
                          <span className="ms-2 text-primary">• {selectedCategories.length} sélectionné(s)</span>
                        )}
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                        {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                          const pageNum = index + 1;
                          return (
                            <Pagination.Item key={pageNum} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)} className={pageNum === currentPage ? 'fw-bold' : ''}>
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        {totalPages > 7 && (
                          <>
                            <Pagination.Ellipsis />
                            <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                          </>
                        )}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                      </Pagination>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* MODAL D'AJOUT */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
            <Modal.Title className="d-flex align-items-center">
              <FolderPlus className="me-2" />Nouvelle Catégorie
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={12}>
                  <FloatingLabel label="Nom de la catégorie *" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Nom de la catégorie"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      required
                    />
                  </FloatingLabel>
                </Col>
                <Col md={12}>
                  <FloatingLabel label="Description" className="mb-3">
                    <Form.Control
                      as="textarea"
                      style={{ height: '120px' }}
                      placeholder="Description de la catégorie (optionnelle)"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    />
                  </FloatingLabel>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={loadingAdd}>
              Annuler
            </Button>
            <Button variant="success" onClick={handleAddCategory} disabled={loadingAdd || !newCategory.name.trim()}>
              {loadingAdd ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><Check className="me-2" />Enregistrer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL D'ÉDITION */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #0dcaf0, #0b5ed7)" }}>
            <Modal.Title className="d-flex align-items-center">
              <Pencil size={20} className="me-2" />Modifier la catégorie
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 py-3">
            {editedCategory && (
              <Form>
                <Row className="g-4">
                  <Col md={12}>
                    <FloatingLabel label="Nom de la catégorie *" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Nom de la catégorie"
                        value={editedCategory.name || ''}
                        onChange={(e) => setEditedCategory({ ...editedCategory, name: e.target.value })}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={12}>
                    <FloatingLabel label="Description" className="mb-3">
                      <Form.Control
                        as="textarea"
                        style={{ height: '120px' }}
                        placeholder="Description de la catégorie"
                        value={editedCategory.description || ''}
                        onChange={(e) => setEditedCategory({ ...editedCategory, description: e.target.value })}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer className="px-4">
            <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} disabled={loadingUpdate}>
              Annuler
            </Button>
            <Button variant="info" className="text-white" onClick={handleSaveEdit} disabled={loadingUpdate || !(editedCategory?.name)?.trim()}>
              {loadingUpdate ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><Check size={18} className="me-2" />Enregistrer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL DE SUPPRESSION */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title><ExclamationCircle className="text-warning me-2" />Confirmer la suppression</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <Trash className="display-4 text-danger mb-3" size={48} />
              <h5>Êtes-vous sûr de vouloir supprimer cette catégorie ?</h5>
              {categoryToDelete && (
                <div className="mt-3 p-3 bg-light rounded">
                  <strong>{categoryToDelete.name}</strong>
                  <br />
                  <small className="text-muted">ID: {categoryToDelete.id} | Code: {categoryToDelete.code_category}</small>
                  <div className="mt-2">{getCategoryBadge(categoryToDelete)}</div>
                </div>
              )}
              <p className="text-muted mt-3">Cette action est irréversible.</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loadingDelete}>
              <X className="me-2" />Annuler
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={loadingDelete}>
              {loadingDelete ? <><Spinner animation="border" size="sm" className="me-2" />Suppression...</> : <><Trash className="me-2" />Supprimer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border-radius: 12px; overflow: hidden; }
          .stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; }
          .card-gradient { position: absolute; top: 0; left: 0; width: 100%; height: 4px; opacity: 0.8; }
          .rounded-circle { border-radius: 50% !important; }
        `}</style>
        <Footer />
      </div>
    </React.Fragment>
  );
}